import { Alert, Button, Collapse, Spin, Table, Typography } from "antd";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";

const { Paragraph, Text } = Typography;

type TaxReview = {
  id: string;
  createdAt: number;
  createdBy?: string;
  dateRange: [number, number];
  taxYearLabel?: string;
  model?: string;
  output?: string;
  input?: {
    summary?: Record<string, any>;
    gst?: Record<string, any>;
    personalTax?: Record<string, any>;
    warnings?: string[];
  };
};

const money = (value: unknown) =>
  typeof value === "number" ? `$${value.toFixed(2)}` : "-";

export const TaxReviewViewer = () => {
  const [reviews, setReviews] = useState<TaxReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "taxReviews"), orderBy("createdAt", "desc"), limit(10));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setReviews(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as TaxReview)),
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  const exportReview = (review: TaxReview) => {
    const blob = new Blob(
      [
        `# Tax Review ${review.taxYearLabel || review.id}\n\n`,
        `Created: ${new Date(review.createdAt).toLocaleString()}\n`,
        `Model: ${review.model || "-"}\n\n`,
        review.output || "No output saved.",
      ],
      { type: "text/markdown;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tax-review-${review.taxYearLabel || review.id}.md`.replace(/\s+/g, "-");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (!reviews.length) {
    return (
      <Alert
        type="info"
        showIcon
        message="No AI tax reviews found"
        description="Open Personal Tax, select a tax year, and click Send selected tax year to AI tax review. Saved reports will appear here."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Alert
        type="info"
        showIcon
        message="Last 10 AI tax review reports"
        description="These are saved from the Personal Tax tab via the taxReview Firebase Function. Expand a report to read the AI output or export it as markdown."
      />

      <Table
        size="small"
        pagination={{ pageSize: 1 }}
        dataSource={reviews.map((review) => ({ ...review, key: review.id }))}
        columns={[
          {
            title: "Review",
            render: (_value, review) => (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <Text strong>{review.taxYearLabel || "Tax review"}</Text>
                    <div>
                      <Text type="secondary">
                        {`${new Date(review.createdAt).toLocaleString()} // ${review.model || "AI model"}` }
                      </Text>
                    </div>
                  </div>
                  <Button onClick={() => exportReview(review)}>Export Markdown</Button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
                  <div>Revenue: {money(review.input?.summary?.revenue)}</div>
                  <div>Expenses: {money(review.input?.summary?.expenses)}</div>
                  <div>Profit Est: {money(review.input?.summary?.profitEstimate)}</div>
                  <div>GST Position: {money(review.input?.gst?.gstPayableOrRefundable)}</div>
                  <div>Income Tax Est: {money(review.input?.personalTax?.incomeTax)}</div>
                  <div>Warnings: {review.input?.warnings?.length || 0}</div>
                </div>

                <Collapse
                  items={[
                    {
                      key: "output",
                      label: "AI recommendation report",
                      children: (
                        <Paragraph style={{ whiteSpace: "pre-wrap", color: "#e5e7eb" }}>
                          {review.output || "No output saved."}
                        </Paragraph>
                      ),
                    },
                    {
                      key: "warnings",
                      label: `Warnings (${review.input?.warnings?.length || 0})`,
                      children: (
                        <ul>
                          {(review.input?.warnings || []).map((warning, idx) => (
                            <li key={`${warning}-${idx}`}>{warning}</li>
                          ))}
                        </ul>
                      ),
                    },
                  ]}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
